import React, { useState, useMemo, useEffect, useCallback } from 'react';
import Swal from 'sweetalert2';
import './GuestPage.css';
import './GuestCasesCalendar.css';
import './GuestCasesCalendarOverride.css';
import authService from '../services/authService';

const GuestCasesCalendar = ({ onBack, token }) => {
  const [currentDate, setCurrentDate] = useState(new Date()); // Current month/year
  const [selectedDate, setSelectedDate] = useState(new Date()); // Today
  const [viewMode, setViewMode] = useState('2weeks'); // '2weeks', '1week', '1month'
  const [searchTerm, setSearchTerm] = useState('');
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleting, setDeleting] = useState(null);

  // Fetch all cases from API to populate calendar
  useEffect(() => {
    const fetchAllCases = async () => {
      try {
        setLoading(true);
        setError('');
        
        console.log('📋 Fetching all guest cases from authService...');
        const data = await authService.getGuestRequests(token);
        
        console.log('📋 Response data:', data);

        // Extract cases from response
        let casesData = [];
        
        if (data && data.data && Array.isArray(data.data)) {
          casesData = data.data;
        } else if (Array.isArray(data)) {
          casesData = data;
        }

        // Transform cases
        let transformedCases = casesData.map(apiCase => {
          const createdDate = new Date(apiCase.created_at);
          const dateStr = `${createdDate.getFullYear()}-${String(createdDate.getMonth() + 1).padStart(2, '0')}-${String(createdDate.getDate()).padStart(2, '0')}`;
          
          return {
            id: apiCase.id,
            date: dateStr,
            title: apiCase.title,
            refName: apiCase.title,
            caseType: apiCase.case_type,
            description: apiCase.description,
            lawyerName: apiCase.lawyer_name || 'Assigned Lawyer',
            caseLocation: apiCase.case_location,
            status: apiCase.status || 'Pending',
            createdAt: apiCase.created_at,
          };
        });

        setCases(transformedCases);
        console.log('📋 Transformed cases:', transformedCases);
      } catch (err) {
        console.error('❌ Error fetching cases:', err);
        console.error('❌ Error message:', err.message);
        setError(err.message || 'Failed to fetch cases');
        setCases([]);
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchAllCases();
    } else {
      setError('Authentication token not found');
      setLoading(false);
    }
  }, [token]);



  // Get the number of days in a month
  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  // Get the first day of the month (0 = Monday, 6 = Sunday for Monday-start calendar)
  const getFirstDayOfMonth = (date) => {
    const day = new Date(date.getFullYear(), date.getMonth(), 1).getDay();
    // Convert from Sunday=0 to Monday=0 format
    return day === 0 ? 6 : day - 1;
  };

  // Get all cases for a specific date
  const getCasesForDate = useCallback((date) => {
    const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    return cases.filter(c => c.date === dateStr);
  }, [cases]);

  // Get cases count for each date (respecting search filter)
  const getCasesCountForDate = useCallback((date) => {
    let filtered = getCasesForDate(date);
    
    // Apply search filter if present
    if (searchTerm.trim()) {
      filtered = filtered.filter(c =>
        c.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.refName.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    return filtered.length;
  }, [getCasesForDate, searchTerm]);

  // Handle previous month
  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  // Handle next month
  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  // Handle date click
  const handleDateClick = (day) => {
    setSelectedDate(new Date(currentDate.getFullYear(), currentDate.getMonth(), day));
  };

  // Get selected date's cases
  const selectedCases = useMemo(() => {
    let filtered = getCasesForDate(selectedDate);
    if (searchTerm.trim()) {
      filtered = filtered.filter(c =>
        c.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.refName.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    return filtered;
  }, [selectedDate, searchTerm, getCasesForDate]);

  // Render calendar grid
  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const days = [];

    // Empty cells for days before month starts
    for (let i = 0; i < firstDay; i++) {
      days.push(
        <div key={`empty-${i}`} className="calendar-day empty"></div>
      );
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
      const casesCount = getCasesCountForDate(date);
      const isSelected = 
        selectedDate.getDate() === day &&
        selectedDate.getMonth() === currentDate.getMonth() &&
        selectedDate.getFullYear() === currentDate.getFullYear();

      days.push(
        <div
          key={day}
          className={`calendar-day ${isSelected ? 'selected' : ''}`}
          onClick={() => handleDateClick(day)}
        >
          <span className="day-number">{day}</span>
          {casesCount > 0 && (
            <span className="cases-badge">{casesCount}</span>
          )}
        </div>
      );
    }

    return days;
  };

  const monthName = currentDate.toLocaleString('default', { month: 'long', year: 'numeric' });

  return (
    <div className="my-cases-container">
      {/* Header */}
      <div className="cases-header">
          <button className="back-button" onClick={onBack}>
            ←
          </button>
          <h2>My Cases</h2>
      </div>

      {/* Search Bar */}
      <div className="search-container">
        <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder="Search by Case Number, Reference Name, Case Title, Phone Number"
            className="search-input"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
      </div>

      <div className="cases-content">
        {/* Calendar Section */}
        <div className="calendar-section">
          {/* Month Navigation */}
          <div className="month-navigation">
            <button className="nav-button" onClick={handlePrevMonth}>
              ←
            </button>
            <span className="month-label">{monthName}</span>
            <button className="nav-button" onClick={handleNextMonth}>
              →
            </button>
          </div>

          {/* View Mode Toggle */}
          <div className="view-mode-toggle">
            <button 
              className={`mode-button ${viewMode === '2weeks' ? 'active' : ''}`}
              onClick={() => setViewMode('2weeks')}
            >
              2 weeks
            </button>
            <button 
              className={`mode-button ${viewMode === '1week' ? 'active' : ''}`}
              onClick={() => setViewMode('1week')}
            >
              1 week
            </button>
            <button 
              className={`mode-button ${viewMode === '1month' ? 'active' : ''}`}
              onClick={() => setViewMode('1month')}
            >
              1 month
            </button>
          </div>

          {/* Day Headers */}
          <div className="calendar-header">
            <div className="day-header">MON</div>
            <div className="day-header">TUE</div>
            <div className="day-header">WED</div>
            <div className="day-header">THU</div>
            <div className="day-header">FRI</div>
            <div className="day-header">SAT</div>
            <div className="day-header">SUN</div>
          </div>

          {/* Calendar Grid */}
          <div className="calendar-grid">
            {renderCalendar()}
          </div>
        </div>

        {/* Cases List Section */}
        <div className="cases-list-section">
          <div className="selected-date-info">
            <h3>
              {selectedDate.toLocaleDateString('default', {
                weekday: 'long',
                month: 'long',
                day: 'numeric',
                year: 'numeric'
              })}
            </h3>
            <span className="case-count">
              {selectedCases.length} {selectedCases.length === 1 ? 'case' : 'cases'}
            </span>
          </div>

          {loading ? (
            <div className="loading-state">
              <p>Loading cases...</p>
            </div>
          ) : error ? (
            <div className="error-state">
              <p>{error}</p>
            </div>
          ) : selectedCases.length > 0 ? (
            <div className="cases-list">
              {selectedCases.map(caseItem => (
                <div key={caseItem.id} className="case-item">
                  <div className="case-header">
                    <div className="case-title-section">
                      <span className="case-number">{caseItem.title}</span>
                      <span className="case-type">{caseItem.caseType}</span>
                    </div>
                    <span className={`case-status ${caseItem.status.toLowerCase()}`}>
                      {caseItem.status}
                    </span>
                  </div>
                  
                  <div className="case-parties">
                    <div className="detail-row">
                      <span className="detail-label">Assigned Lawyer:</span>
                      <span className="detail-value" style={{fontWeight: 600, color: '#f39c12'}}>{caseItem.lawyerName}</span>
                    </div>
                  </div>

                  <div className="case-details">
                    {caseItem.caseLocation && (
                      <div className="detail-row">
                        <span className="detail-label">Location:</span>
                        <span className="detail-value">{caseItem.caseLocation}</span>
                      </div>
                    )}
                    {caseItem.description && (
                      <div className="detail-row full-width">
                        <span className="detail-label">Description:</span>
                        <p className="detail-value description">{caseItem.description}</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-cases">
              <p>No cases found for this date</p>
              {searchTerm && (
                <button 
                  className="clear-search-button"
                  onClick={() => setSearchTerm('')}
                >
                  Clear Search
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GuestCasesCalendar;
